import { Helmet } from 'react-helmet-async';
import { logEvent } from 'firebase/analytics';
import { useLocation } from 'react-router-dom';
import { Spinner } from '@uni-of-exeter/digital.lib.react.ui.kit';
import { useMemo, useState, useEffect, useLayoutEffect } from 'react';

import { analytics } from '../firebase';
import './styles/recommendation-pageV2.css';
import { APP_TEXT } from '../const/app-text';
import { formatDate } from '../utils/format-time';
import { useLibraryFilters } from '../hooks/utils';
import { useAuthContext } from '../auth/auth-provider';
import { PageTracker } from '../components/page-tracker';
import FeedbackBlock from '../components/feedback-block';
import { useGetHistory } from '../hooks/query/learning-api/use-quiz-session';
import { useGetUserProfiles } from '../hooks/query/learning-api/use-user-profile';
import { FilterTopicType } from '../components/filters/digital-skills-topic-filter';
import { RecommendationsV2 } from '../components/recommendations/recommendationsV2';
import { useGetMultipleSkillCategoriesById } from '../hooks/query/learning-api/use-skill-category';
import {
  useGetResourceFacets,
  useGetRecommendations,
} from '../hooks/query/learning-api/use-resource';
import {
  getDistinctTopics,
  SkillCategoryAssessments,
  buildRecommendationGroups,
} from '../utils/recommendation-helpers';

const pageText = APP_TEXT.pages.recommendation;

const RecommendationPageV2 = () => {
  const location = useLocation();
  const { userId } = useAuthContext();

  const historyQuery = useGetHistory();
  const userProfileQuery = useGetUserProfiles();
  const facetsQuery = useGetResourceFacets();
  const recommendationsQuery = useGetRecommendations();
  const [selectedTopicFilters, setSelectedTopicFilters] = useState<FilterTopicType[]>([]);

  const {
    filters,
    updateFilters,
    resetFilters,
    hasActiveFilters,
    uniqueSkillCategoryOptions,
    mediaTypeOptions,
    locationOptions,
  } = useLibraryFilters(facetsQuery.data);

  const currentHistory = useMemo(() => {
    if (historyQuery.data && historyQuery.data.length > 0) {
      return historyQuery.data.at(-1);
    }
  }, [historyQuery.data]);

  const skillCategoriesInfoQuery = useGetMultipleSkillCategoriesById(
    Object.keys(currentHistory?.skillCategoryAssessments ?? {})
  );

  const groups = useMemo(() => {
    if (
      !skillCategoriesInfoQuery.data ||
      !recommendationsQuery.data ||
      recommendationsQuery.data.length === 0
    ) {
      return [];
    }

    let groups = buildRecommendationGroups(
      recommendationsQuery.data,
      skillCategoriesInfoQuery.data,
      (currentHistory?.skillCategoryAssessments ?? []) as SkillCategoryAssessments,
      selectedTopicFilters,
      filters
    );

    groups = groups.filter((group) =>
      group.resources.some((resource) => resource.recommendationHidden !== true)
    );
    return groups;
  }, [
    skillCategoriesInfoQuery.data,
    currentHistory,
    recommendationsQuery.data,
    selectedTopicFilters,
    filters,
  ]);

  const distinctTopics = useMemo(() => {
    return getDistinctTopics(recommendationsQuery?.data ?? []);
  }, [recommendationsQuery.data]);

  const selectedGroupTitle = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('skillGroupTitle')?.trim() ?? '';
  }, [location.search]);

  const handleTopicChange = (topic: FilterTopicType) => {
    if (selectedTopicFilters.some((t: FilterTopicType) => t.id === topic.id)) {
      setSelectedTopicFilters(
        selectedTopicFilters.filter(
          (selectedTopic: FilterTopicType) => selectedTopic.id !== topic.id
        )
      );
    } else {
      setSelectedTopicFilters([...selectedTopicFilters, topic]);
      const action = 'filter_recommendation_by_topic';
      logEvent(analytics, action, {
        selected_topic_id: topic.id,
        custom_user_id: userId,
      });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        logEvent(analytics, 'search_recommendation', {
          search_query: filters.searchQuery,
          custom_user_id: userId,
        });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.searchQuery, userId]);

  useEffect(() => {
    if (filters.locations && filters.locations.length > 0) {
      logEvent(analytics, 'filter_recommendation_by_location', {
        selected_locations: filters.locations.map((location) => location).join(', '),
        custom_user_id: userId,
      });
    }
  }, [filters.locations, userId]);

  useEffect(() => {
    if (filters.mediaTypes && filters.mediaTypes.length > 0) {
      logEvent(analytics, 'filter_recommendation_by_media_type', {
        selected_media_types: filters.mediaTypes.map((mediaType) => mediaType).join(', '),
        custom_user_id: userId,
      });
    }
  }, [filters.mediaTypes, userId]);

  useEffect(() => {
    if (filters.skillCategoryIds && filters.skillCategoryIds.length > 0) {
      logEvent(analytics, 'filter_recommendation_by_skill_category', {
        selected_skill_categories: filters.skillCategoryIds
          .map((skillCategoryId) => skillCategoryId)
          .join(', '),
        custom_user_id: userId,
      });
    }
  }, [filters.skillCategoryIds, userId]);

  useLayoutEffect(() => {
    if (!selectedGroupTitle || groups.length === 0) {
      return;
    }

    const matchingGroup = groups.find(
      (group) => group.title.trim().toLowerCase() === selectedGroupTitle.trim().toLowerCase()
    );

    if (!matchingGroup) {
      return;
    }

    const targetElement = document.getElementById(
      `recommendation-group-${matchingGroup.title.replace(/\s+/g, '-').toLowerCase()}`
    );

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedGroupTitle, groups]);

  return (
    <>
      <PageTracker
        pageName="Recommendation Page"
        userId={userId}
      />

      <Helmet>
        <title>{pageText.pageTitle}</title>
      </Helmet>

      {(historyQuery.isLoading ||
        skillCategoriesInfoQuery.isLoading ||
        recommendationsQuery.isLoading) && (
        <div className="flexCenter">
          <Spinner aria-label={pageText.loadingAriaLabel} />
        </div>
      )}
      {!(
        historyQuery.isLoading ||
        skillCategoriesInfoQuery.isLoading ||
        recommendationsQuery.isLoading
      ) && (
        <>
          <div className="recommendationPageV2">
            <div className="recommendationPageContent">
              <RecommendationsV2
                title={pageText.title}
                completedOn={formatDate(currentHistory?.completed ?? new Date())}
                groups={groups}
                filterTopics={distinctTopics}
                selectedTopics={selectedTopicFilters}
                onTopicChange={handleTopicChange}
                managerEmail={userProfileQuery.data?.managerEmail ?? ''}
                userFullName={`${userProfileQuery.data?.firstName ?? ''} ${userProfileQuery.data?.lastName ?? ''}`}
                userEmail={userProfileQuery.data?.email ?? ''}
                showShareToManager={
                  userProfileQuery.isSuccess && !!userProfileQuery.data?.managerEmail
                }
                filters={{
                  filters,
                  updateFilters,
                  resetFilters,
                  hasActiveFilters,
                  skillOptions: uniqueSkillCategoryOptions,
                  mediaTypeOptions,
                  locationOptions,
                  searchPlaceholder: pageText.searchPlaceholder,
                }}
              />
            </div>
          </div>
          <div className="recommendationPageFeedbackContent">
            <FeedbackBlock />
          </div>
        </>
      )}
    </>
  );
};

export default RecommendationPageV2;
